// pages/CreateGreetingCard.js - Greeting Card Creation/Edit Page
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX,
  FiImage,
  FiType,
  FiMail,
  FiDroplet,
  FiCheck,
  FiGlobe,
  FiLock,
  FiUsers
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import {
  createGreetingCard,
  updateGreetingCard,
  getGreetingCardById,
  uploadCardCoverImage
} from '../services/greetingCardService';
import { CARD_STYLES } from '../constants/cardStyles';
import { CARD_FONTS } from '../constants/cardFonts';
import { ENVELOPE_STYLES } from '../constants/envelopeStyles';
import { CARD_VIDEO_THEMES } from '../constants/cardVideoThemes';
import { useAuth } from '../services/auth';
import CardCoverImageModal from '../components/CardCoverImageModal';
import AddCardGuestsModal from '../components/AddCardGuestsModal';
import '../styles/CreateGreetingCard.scss';

import { cardUrl } from '../shared/shortLinks';
const CreateGreetingCard = () => {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  // Basic State
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);

  // Style State
  const [selectedStyle, setSelectedStyle] = useState(CARD_STYLES[0]);
  const [selectedFont, setSelectedFont] = useState(CARD_FONTS[0]);
  const [selectedEnvelope, setSelectedEnvelope] = useState(ENVELOPE_STYLES[0]);

  // Theme State
  const [themeType, setThemeType] = useState('color');
  const [themeColor, setThemeColor] = useState('#7C4DFF');
  const [themeVideoUrl, setThemeVideoUrl] = useState(null);
  const [themeVideoThumbnail, setThemeVideoThumbnail] = useState(null);

  // Privacy State
  const [privacy, setPrivacy] = useState('public');
  const [guestListIds, setGuestListIds] = useState([]);

  // UI State
  const [showCoverImageModal, setShowCoverImageModal] = useState(false);
  const [showGuestListModal, setShowGuestListModal] = useState(false);
  const [showStyleSelector, setShowStyleSelector] = useState(false);
  const [showFontSelector, setShowFontSelector] = useState(false);
  const [showEnvelopeSelector, setShowEnvelopeSelector] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showVideoThemeSelector, setShowVideoThemeSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCard, setLoadingCard] = useState(false);
  const [errors, setErrors] = useState({});

  const isEditMode = !!cardId;

  // Load existing card for editing
  useEffect(() => {
    if (isEditMode) {
      loadCard();
    }
  }, [cardId]);

  const loadCard = async () => {
    try {
      setLoadingCard(true);
      const card = await getGreetingCardById(cardId);

      // Verify ownership
      if (card.creator_id !== user.id) {
        alert('You can only edit your own cards');
        navigate('/greeting-cards');
        return;
      }

      // Load card data
      setTitle(card.title || '');
      setNotes(card.notes || '');
      setCoverImagePreview(card.cover_image_cdn_url);
      setSelectedStyle(CARD_STYLES.find(s => s.id === card.card_style) || CARD_STYLES[0]);
      setSelectedFont(CARD_FONTS.find(f => f.id === card.font_family) || CARD_FONTS[0]);
      setSelectedEnvelope(ENVELOPE_STYLES.find(e => e.id === card.envelope_style) || ENVELOPE_STYLES[0]);
      setThemeType(card.theme_type || 'color');
      setThemeColor(card.theme_color || '#7C4DFF');
      setPrivacy(card.privacy || 'public');
      setGuestListIds(card.guest_list_ids || []);
    } catch (error) {
      console.error('Error loading card:', error);
      alert('Failed to load card');
      navigate('/greeting-cards');
    } finally {
      setLoadingCard(false);
    }
  };

  const handleImageSelect = (imageData) => {
    console.log('Image selected:', imageData);

    // Close modal
    setShowCoverImageModal(false);

    // Handle different image types
    if (imageData.type === 'template') {
      // Template selected - apply all template settings
      setCoverImage(imageData);
      setCoverImagePreview(imageData.uri);
      setThemeColor(imageData.themeColor);
      setThemeType('color');

      // Apply template defaults
      if (imageData.defaultTitle && !title) {
        setTitle(imageData.defaultTitle);
      }
      if (imageData.defaultNotes && !notes) {
        setNotes(imageData.defaultNotes);
      }

      // Apply font and envelope
      const font = CARD_FONTS.find(f => f.id === imageData.fontFamily) || CARD_FONTS[0];
      setSelectedFont(font);

      const envelope = ENVELOPE_STYLES.find(e => e.id === imageData.envelopeStyle) || ENVELOPE_STYLES[0];
      setSelectedEnvelope(envelope);

    } else if (imageData.type === 'youtube') {
      // YouTube video selected
      setCoverImage(imageData);
      setCoverImagePreview(imageData.thumbnailUri);

    } else if (imageData.type === 'gif') {
      // GIF selected
      setCoverImage(imageData);
      setCoverImagePreview(imageData.uri);
      if (imageData.file) {
        setCoverImageFile(imageData.file);
      }

    } else {
      // Regular image
      setCoverImage(imageData);
      setCoverImagePreview(imageData.uri);
      if (imageData.file) {
        setCoverImageFile(imageData.file);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    } else if (title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    if (notes.length > 500) {
      newErrors.notes = 'Message must be less than 500 characters';
    }

    if (!coverImagePreview) {
      newErrors.coverImage = 'Cover image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      let coverImageUrl = coverImagePreview;

      // Upload new image if file selected
      if (coverImageFile) {
        const uploadResult = await uploadCardCoverImage(
          coverImageFile,
          cardId || 'temp-' + Date.now()
        );
        coverImageUrl = uploadResult.cdnUrl;
      }

     const cardData = {
  title: title.trim(),
  notes: notes.trim(),
  cover_image_type: coverImage?.type || 'image',
  theme_type: themeType,
  theme_color: themeColor,
  card_style: selectedStyle.id,
  font_family: selectedFont.id,
  envelope_style: selectedEnvelope.id,
  privacy,
  guest_list_ids: privacy === 'private' ? guestListIds : [],
};

// Handle cover image upload based on type
if (coverImageFile) {
  const uploadResult = await uploadCardCoverImage(
    coverImageFile,
    cardId || 'temp-' + Date.now()
  );

  if (uploadResult.type === 'gif') {
    cardData.cover_image_type = 'gif';
    cardData.cover_gif_cdn_url = uploadResult.gifCdnUrl;
    cardData.cover_static_gif_cdn_url = uploadResult.staticCdnUrl;
    cardData.cover_image_cdn_url = uploadResult.gifCdnUrl;
    cardData.cover_thumbnail_cdn_url = uploadResult.staticCdnUrl;
  } else {
    cardData.cover_image_cdn_url = uploadResult.cdnUrl;
    cardData.cover_thumbnail_cdn_url = uploadResult.cdnUrl;
  }
} else if (coverImage?.type === 'youtube') {
  cardData.cover_image_type = 'youtube';
  cardData.youtube_video_id = coverImage.videoId;
  cardData.youtube_video_url = coverImage.originalUrl;
  cardData.youtube_thumbnail_cdn_url = coverImage.thumbnailUri;
  cardData.cover_thumbnail_cdn_url = coverImage.thumbnailUri;
} else if (coverImage?.type === 'template') {
  cardData.cover_image_type = 'template';
  cardData.cover_image_cdn_url = coverImage.uri;
  cardData.cover_thumbnail_cdn_url = coverImage.uri;
} else if (coverImagePreview) {
  cardData.cover_image_cdn_url = coverImagePreview;
  cardData.cover_thumbnail_cdn_url = coverImagePreview;
}

// Handle video theme
if (themeType === 'video' && themeVideoUrl) {
  cardData.theme_video_cdn_url = themeVideoUrl;
  cardData.theme_thumbnail_cdn_url = themeVideoThumbnail;
}

      if (isEditMode) {
        await updateGreetingCard(cardId, cardData);
        alert('Card updated successfully!');
        navigate('/greeting-cards');
      } else {
        const { card, slug } = await createGreetingCard(cardData);
        const shareUrl = cardUrl(slug, window.location.origin);

        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(shareUrl);
          alert(`Card created successfully!\n\nShare link copied to clipboard:\n${shareUrl}`);
        } catch (err) {
          alert(`Card created successfully!\n\nShare link:\n${shareUrl}`);
        }

        navigate(`/c/${slug}`);
      }
    } catch (error) {
      console.error('Error saving card:', error);
      alert('Failed to save card. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingCard) {
    return (
      <DashboardLayout>
        <div className="create-greeting-card-page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading card...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="create-greeting-card-page">
        {/* Header */}
        <div className="page-header">
          <button className="btn-back" onClick={() => navigate('/greeting-cards')}>
            <FiX />
          </button>
          <h1>{isEditMode ? 'Edit Card' : 'Create Greeting Card'}</h1>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
          </button>
        </div>

        <div className="create-content">
          {/* Left: Preview */}
          <div className="preview-section">
            <div className="preview-card-wrapper">
              {/* Envelope Preview */}
              <div
                className="envelope-preview"
                style={{
                  background: `linear-gradient(135deg, ${selectedEnvelope.bodyColors.join(', ')})`,
                  borderColor: selectedEnvelope.borderColor,
                }}
              >
                <div
                  className="ribbon-vertical"
                  style={{ backgroundColor: selectedEnvelope.ribbonColor }}
                />
                <div
                  className="ribbon-horizontal"
                  style={{ backgroundColor: selectedEnvelope.ribbonColor }}
                />
                <div className="bow-center">
                  <div
                    className="bow-knot"
                    style={{
                      backgroundColor: selectedEnvelope.bowColor,
                      borderColor: selectedEnvelope.ribbonColor,
                    }}
                  >
                    <div
                      className="bow-left"
                      style={{ backgroundColor: selectedEnvelope.bowColor }}
                    />
                    <div
                      className="bow-right"
                      style={{ backgroundColor: selectedEnvelope.bowColor }}
                    />
                  </div>
                </div>
              </div>

              {/* Card Preview */}
              <div
                className="card-preview"
                style={{
                  backgroundColor: themeColor,
                  borderRadius: `${selectedStyle.borderRadius}px`,
                  borderWidth: `${selectedStyle.borderWidth}px`,
                  borderColor: selectedStyle.borderColor,
                  borderStyle: 'solid',
                  fontFamily: selectedFont.fontFamily,
                }}
              >
                {coverImagePreview && (
                  <img
                    src={coverImagePreview}
                    alt="Cover"
                    className="preview-cover-image"
                  />
                )}
                {title && (
                  <div className="preview-overlay">
                    <h3 style={{ fontFamily: selectedFont.fontFamily }}>{title}</h3>
                    {notes && <p style={{ fontFamily: selectedFont.fontFamily }}>{notes}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Editor */}
          <div className="editor-section">
           {/* Cover Image */}
<div className="form-group">
  <label>Cover Image *</label>
  <div className="cover-image-selector">
    {coverImagePreview ? (
      <div className="cover-image-preview">
        <img src={coverImagePreview} alt="Cover" />
        {/* Show GIF badge if it's a GIF */}
        {coverImage?.type === 'gif' && (
          <div className="media-badge">🎬 GIF</div>
        )}
        {/* Show YouTube badge if it's YouTube */}
        {coverImage?.type === 'youtube' && (
          <div className="media-badge">▶️ YouTube</div>
        )}
        <button
          className="btn-change"
          onClick={() => setShowCoverImageModal(true)}
        >
          <FiImage /> Change Image
        </button>
      </div>
    ) : (
      <button
        className="btn-upload"
        onClick={() => setShowCoverImageModal(true)}
      >
        <FiImage />
        <span>Upload Cover Image</span>
      </button>
    )}
  </div>
  {errors.coverImage && <p className="error">{errors.coverImage}</p>}
</div>


            {/* Title */}
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Enter card title"
                maxLength={100}
              />
              <span className="char-count">{title.length}/100</span>
              {errors.title && <p className="error">{errors.title}</p>}
            </div>

            {/* Message */}
            <div className="form-group">
              <label>Message</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Write your message here..."
                rows={4}
                maxLength={500}
              />
              <span className="char-count">{notes.length}/500</span>
              {errors.notes && <p className="error">{errors.notes}</p>}
            </div>

            {/* Privacy */}
            <div className="form-group">
              <label>Privacy</label>
              <div className="privacy-options">
                <button
                  className={`privacy-btn ${privacy === 'public' ? 'active' : ''}`}
                  onClick={() => setPrivacy('public')}
                >
                  <FiGlobe /> Public
                </button>
                <button
                  className={`privacy-btn ${privacy === 'private' ? 'active' : ''}`}
                  onClick={() => setPrivacy('private')}
                >
                  <FiLock /> Private
                </button>
              </div>
              {privacy === 'private' && (
                <p className="privacy-note">
                  <FiUsers /> Private cards can only be viewed by people you invite
                </p>
              )}
            </div>

            {privacy === 'private' && (
  <button
    className="guest-list-button"
    onClick={() => setShowGuestListModal(true)}
  >
    <FiUsers />
    <span>
      {guestListIds.length > 0
        ? `${guestListIds.length} ${guestListIds.length === 1 ? 'person' : 'people'} can access`
        : 'Add people who can access'}
    </span>
  </button>
)}


            {/* Style Selectors */}
           <div className="style-selectors">
  <button
    className="style-selector-btn"
    onClick={() => setShowThemeSelector(true)}
  >
    <div
      className="selector-preview"
      style={{ backgroundColor: themeColor }}
    />
    <span>Color Theme</span>
  </button>

  <button
    className="style-selector-btn"
    onClick={() => setShowVideoThemeSelector(true)}
  >
    <FiDroplet className="selector-icon" />
    <span>Video Theme</span>
  </button>

              <button
                className="style-selector-btn"
                onClick={() => setShowFontSelector(true)}
              >
                <FiType className="selector-icon" />
                <span>Font</span>
              </button>

              <button
                className="style-selector-btn"
                onClick={() => setShowEnvelopeSelector(true)}
              >
                <FiMail className="selector-icon" />
                <span>Envelope</span>
              </button>
            </div>
          </div>
        </div>

        {/* Theme Selector Modal */}
        <AnimatePresence>
          {showThemeSelector && (
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowThemeSelector(false)}
            >
              <motion.div
                className="modal-content selector-modal"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Select Theme Color</h3>
                  <button onClick={() => setShowThemeSelector(false)}>
                    <FiX />
                  </button>
                </div>
                <div className="color-grid">
                  {[
                    '#7C4DFF', '#FF6B6B', '#4ECDC4', '#FFD93D',
                    '#6BCF7F', '#FF69B4', '#9B59B6', '#3498DB',
                    '#E74C3C', '#1ABC9C', '#F39C12', '#2C3E50'
                  ].map(color => (
                    <button
                      key={color}
                      className={`color-option ${themeColor === color ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => {
                        setThemeColor(color);
                        setShowThemeSelector(false);
                      }}
                    >
                      {themeColor === color && <FiCheck />}
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Font Selector Modal */}
        <AnimatePresence>
          {showFontSelector && (
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFontSelector(false)}
            >
              <motion.div
                className="modal-content selector-modal"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Select Font</h3>
                  <button onClick={() => setShowFontSelector(false)}>
                    <FiX />
                  </button>
                </div>
                <div className="font-list">
                  {CARD_FONTS.map(font => (
                    <button
                      key={font.id}
                      className={`font-option ${selectedFont.id === font.id ? 'active' : ''}`}
                      style={{ fontFamily: font.fontFamily }}
                      onClick={() => {
                        setSelectedFont(font);
                        setShowFontSelector(false);
                      }}
                    >
                      <span>{font.name}</span>
                      {selectedFont.id === font.id && <FiCheck />}
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Envelope Selector Modal */}
        <AnimatePresence>
          {showEnvelopeSelector && (
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowEnvelopeSelector(false)}
            >
              <motion.div
                className="modal-content selector-modal"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Select Envelope Style</h3>
                  <button onClick={() => setShowEnvelopeSelector(false)}>
                    <FiX />
                  </button>
                </div>
                <div className="envelope-grid">
                  {ENVELOPE_STYLES.map(envelope => (
                    <button
                      key={envelope.id}
                      className={`envelope-option ${selectedEnvelope.id === envelope.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedEnvelope(envelope);
                        setShowEnvelopeSelector(false);
                      }}
                    >
                      <div
                        className="envelope-preview-small"
                        style={{
                          background: `linear-gradient(135deg, ${envelope.bodyColors.join(', ')})`,
                        }}
                      />
                      <span>{envelope.name}</span>
                      {selectedEnvelope.id === envelope.id && <FiCheck className="check" />}
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Card Cover Image Modal */}
<CardCoverImageModal
  visible={showCoverImageModal}
  onClose={() => setShowCoverImageModal(false)}
  onSelectImage={handleImageSelect}
/>

{/* Guest List Modal */}
{privacy === 'private' && (
  <AddCardGuestsModal
    visible={showGuestListModal}
    onClose={() => setShowGuestListModal(false)}
    selectedUserIds={guestListIds}
    onUsersSelected={setGuestListIds}
  />
)}

{/* Video Theme Selector Modal */}
<AnimatePresence>
  {showVideoThemeSelector && (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => setShowVideoThemeSelector(false)}
    >
      <motion.div
        className="modal-content selector-modal"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Select Video Theme</h3>
          <button onClick={() => setShowVideoThemeSelector(false)}>
            <FiX />
          </button>
        </div>
        <div className="video-themes-grid">
          {CARD_VIDEO_THEMES.map(theme => (
            <button
              key={theme.id}
              className="video-theme-option"
              onClick={() => {
                setThemeType('video');
                setThemeVideoUrl(theme.videoUrl);
                setThemeVideoThumbnail(theme.thumbnailUrl);
                setThemeColor(theme.defaultColor);
                setShowVideoThemeSelector(false);
              }}
            >
              <img src={theme.thumbnailUrl} alt={theme.name} />
              <span>{theme.name}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

      </div>
    </DashboardLayout>
  );
  
};

export default CreateGreetingCard;
